import React, { useState } from 'react';
import { MailboxSourceCard } from './sources/MailboxSourceCard';
import { PortalSourceCard } from './sources/PortalSourceCard';
import { BulkUploadSourceCard } from './sources/BulkUploadSourceCard';
import { DriveSourceCard } from './sources/DriveSourceCard';
import { SMTPSourceCard } from './sources/SMTPSourceCard';
import { APISourceCard } from './sources/APISourceCard';
import { ManualSourceCard } from './sources/ManualSourceCard';
import { Customer } from '@/hooks/useCustomers';

interface SourceType {
  id: string;
  name: string;
  icon: string;
  default: boolean;
  description: string;
}

interface CustomerAllSourcesViewProps {
  customer: Customer | null;
  activeSourceType: string;
  sourceTypes: SourceType[];
}

export const CustomerAllSourcesView: React.FC<CustomerAllSourcesViewProps> = ({ 
  customer, 
  activeSourceType, 
  sourceTypes 
}) => {
  const [setupMode, setSetupMode] = useState<string | null>(null);

  if (!customer) {
    return (
      <div className="flex-1 flex items-center justify-center bg-card">
        <div className="text-center">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-medium text-foreground mb-2">Select a customer</h3>
          <p className="text-muted-foreground max-w-md">
            Choose a customer from the list to manage their data sources. 
            Each customer can have multiple sources configured for bill collection.
          </p>
        </div>
      </div>
    );
  }

  const renderSourceCard = (sourceType: SourceType) => {
    const props = { customer, onSetupMode: setSetupMode };
    
    switch (sourceType.id) {
      case 'mailbox': return <MailboxSourceCard {...props} />;
      case 'portal': return <PortalSourceCard {...props} />;
      case 'bulk': return <BulkUploadSourceCard {...props} />;
      case 'drive': return <DriveSourceCard {...props} />;
      case 'smtp': return <SMTPSourceCard {...props} />;
      case 'api': return <APISourceCard {...props} />;
      case 'manual': return <ManualSourceCard {...props} />;
      default: return null;
    }
  };

  const sourcesToShow = activeSourceType === 'all' 
    ? sourceTypes 
    : sourceTypes.filter(s => s.id === activeSourceType);

  return (
    <div className="flex-1 bg-card overflow-y-auto">
      <div className="p-6">
        {/* Customer Header */}
        <div className="border-b border-border pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-foreground">{customer.name}</h2>
              <p className="text-muted-foreground mt-1">{customer.email}</p>
              <div className="flex gap-2 mt-3">
                <span className="px-2 py-1 bg-green-500/10 text-green-600 text-xs rounded font-medium">
                  ✅ Active Customer
                </span>
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-medium">
                  ID: {customer.id.slice(0, 8)}
                </span>
                <span className="px-2 py-1 bg-purple-500/10 text-purple-600 text-xs rounded font-medium">
                  📧 bills+{customer.name.toLowerCase().replace(/\s+/g, '')}@billportal.ai
                </span>
              </div>
            </div>
            <div className="text-right">
              <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 text-sm">
                + Add New Source
              </button>
            </div>
          </div>
        </div>

        {/* Sources Grid */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-foreground">
              Data Sources 
              {activeSourceType !== 'all' && (
                <span className="text-sm font-normal text-muted-foreground">
                  - {sourceTypes.find(s => s.id === activeSourceType)?.name}
                </span>
              )}
            </h3>
            <div className="flex gap-2">
              <button className="text-sm text-primary hover:text-primary/80">
                Test All Sources
              </button>
              <button className="text-sm text-muted-foreground hover:text-foreground">
                Export Configuration
              </button>
            </div>
          </div>
          
          {/* Source Cards */}
          <div className="grid gap-4">
            {sourcesToShow.map(sourceType => (
              <div key={sourceType.id}>
                {renderSourceCard(sourceType)}
              </div>
            ))}
          </div>

          {/* Quick Setup Actions */}
          <div className="mt-8 p-4 bg-primary/5 rounded-lg">
            <h4 className="font-medium text-primary mb-2">Quick Setup Actions</h4>
            <div className="flex gap-2 flex-wrap">
              <button 
                onClick={() => setSetupMode('portal')}
                className="bg-primary text-primary-foreground px-3 py-2 rounded text-sm hover:bg-primary/90"
              >
                🏢 Setup Portal Integration
              </button>
              <button 
                onClick={() => setSetupMode('bulk')}
                className="bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700"
              >
                📁 Enable Bulk Upload
              </button>
              <button className="border border-primary/20 text-primary px-3 py-2 rounded text-sm hover:bg-primary/5">
                ☁️ Connect Drive
              </button>
              <button className="border border-primary/20 text-primary px-3 py-2 rounded text-sm hover:bg-primary/5">
                🔗 Setup API
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};